import Controller, { OnAfterFetch, OnAfterNavigate, OnBeforeFetch, OnBeforeSubmit, OnBlur, OnChange, OnMount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { CheckboxAPI, FetchAPI, FormAPI, InputAPI, LocatorFieldAPI, RadioGroupAPI, SelectableItem,  } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';

import { HealthHistoryMedicinesCreateAliases as Alias, FragmentNames } from '../../../../codegen';

import { WizardSteps } from '../../../../constants/wizards';
import { DomainType, HealthHistoryType, SearchType } from '../../health-history-types';
import { HealthHistoryListContext } from '../../service/health-history-list-service';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { YesNoType } from '../../../../utils/enum-utils';
import HealthHistoryMedicinesHandle from '../health-history-medicines-commons/health-history-medicines-handle';

@Controller({ component: Alias.LAYOUT_HEALTH_HISTORY_MEDICINES, fragment: FragmentNames.HEALTH_HISTORY_MEDICINES_CREATE })
export default class HealthHistoryMedicinesCreateController extends HealthHistoryMedicinesHandle {

  @OnMount(Alias.WIZARD)
  handleMountWizard(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    this.context.set({ encounterId, patientId });
    InformationBarUtils.collapsedBar = true;
    HealthHistoryListContext.currentTab = HealthHistoryType.MEDICINES;
    this.getPatientInfo();
  }

  @OnMount(Alias.FORM_ONE)
  handleMountFormOne(event: MountEvent): void {
    const api = event.target as FormAPI;
    api.setData({...api.getData(), alert: true });
    this.handleJustificationForEnd();
  }

  @OnMount(Alias.FIELD_UNIT_MEASURE)
  handleMountUnityMeasure(): void {
    this.initUnityMeasure();
  }

  @OnMount(Alias.FIELD_CHANNEL_ADM)
  handleMountAdmChannel(): void {
    this.initAdmChannel();
  }

  @OnMount({ target: Alias.RADIO_GROUP_CONDITION })
  handleMountRadioGroup(event: MountEvent): void {
    this.mountRadioGroupValues(event.target as RadioGroupAPI)
  }

  @OnAfterNavigate(Alias.WIZARD)
  handleOnMountWizardStepTwo(): void {
    this.mountWizardSteps();
  }

  @OnValidate(Alias.FORM_ONE)
  handleValidateFormOne(validationFormOne: ValidateEvent): void {
    this.handleStepDone(validationFormOne.valid, WizardSteps.STEP_ONE);
  }

  @OnValidate(Alias.FORM_TWO)
  handleValidateFormTwo(validationFormTwo: ValidateEvent): void {
    this.handleStepDone(validationFormTwo.valid, WizardSteps.STEP_TWO);
  }

  @OnValidate(Alias.FORM_THREE)
  handleValidateFormThree(validationFormThree: ValidateEvent): void {
    this.handleStepDone(validationFormThree.valid, WizardSteps.STEP_THREE);
  }

  @OnBeforeFetch(Alias.FETCH_UNITY_MEASURE)
  handleBeforeFetchUnityMeasure(event: FetchEvent): void {
    const fetchApi = event.target as FetchAPI;
    const medicineCode = this.context.get('medicineCode');
    const medicineDescription = this.context.get('medicineDescription');
    if (medicineCode) {
      fetchApi.setParameters({ medicineCode })
    }
    if (medicineDescription) {
      fetchApi.setParameters({ medicineDescription })
    }
  }

  @OnBeforeFetch(Alias.FETCH_CHANNEL_ADM)
  handleBeforeFetchChannelAdm(event: FetchEvent): void {
    const fetchApi = event.target as FetchAPI;
    const medicineCode = this.context.get('medicineCode');

    if (medicineCode) {
      fetchApi.setParameters({ medicineCode })
    } else {
      fetchApi.setParameters({ })
    }
  }

  @OnAfterFetch(Alias.FETCH_DATE_TYPE)
  handleFetchDateType(event: FetchEvent): void {
    const dateTypeDefault = (event.data as Record<string, Value>[])[1];
    this.context.set({ initialDateType: dateTypeDefault });
    this.mountDateTypeAfterFetch();
  }

  @OnAfterFetch(Alias.FETCH_UNITY_MEASURE)
  handleFetchUnityMeasure(event: FetchEvent): void {
    this.mountUnityMeasureAfterFetch(event.data as Record<string, Value>[]);
  }

  @OnAfterFetch(Alias.FETCH_CHANNEL_ADM)
  handleFetchChannelAdm(event: FetchEvent): void {
    this.mountChannelAdmAfterFetch(event.data as Record<string, Value>[]);
  }

  @OnChange(Alias.AUTOCOMPLETE_DATE_TYPE)
  handleChangeDateType(event: ChangeEvent<DomainType>): void {
    const dateType = event.value;
    const oldDateType = event?.oldValue;
    this.handleDateTypeFields(dateType, oldDateType);
  }

  @OnChange(Alias.LOCATOR_MEDICINE)
  handleChangeLocator(event: ChangeEvent<LocatorFieldAPI>): void {
    const medicine = event.value as unknown as SearchType;
    this.handleChangeMedicineLocator(medicine);
  }

  @OnChange(Alias.CHECKBOX_UNKNOWN_INFO_MEDICINE)
  handleChangeUnknownInfo(event: ChangeEvent<CheckboxAPI>): void {
    this.changeUnknownChecks(Alias.CHECKBOX_UNKNOWN_INFO_MEDICINE, event.value as unknown as boolean);
  }

  @OnChange(Alias.CHECKBOX_UNKNOWN_END)
  handleChangeUnknownEnd(event: ChangeEvent<CheckboxAPI>): void {
    this.changeUnknownChecks(Alias.CHECKBOX_UNKNOWN_END, event.value as unknown as boolean);
  }

  @OnChange(Alias.CHECKBOX_UNKNOWN_LAST_DOSE)
  handleChangeUnknownLastDose(event: ChangeEvent<CheckboxAPI>): void {
    this.changeUnknownChecks(Alias.CHECKBOX_UNKNOWN_LAST_DOSE, event.value as unknown as boolean);
  }

  @OnChange(Alias.CHECKBOX_UNKNOWN_START)
  handleChangeUnknownStart(event: ChangeEvent<CheckboxAPI>): void {
    this.changeUnknownChecks(Alias.CHECKBOX_UNKNOWN_START, event.value as unknown as boolean);
  }

  @OnChange(Alias.RADIO_GROUP_CONDITION)
  handleChangeRadioGroup(event: ChangeEvent<RadioGroupAPI>): void {
    const value = event.value as unknown as SelectableItem;
    const deniesMedicines = value.key === YesNoType.YES;
    this.context.set({ deniesMedicines })
    this.handleFieldsMedicineContinuousUse(deniesMedicines);
  }

  @OnChange(Alias.INPUT_UNREGISTRED_MEDICINE)
  handleInputUnregisterdMed(event: ChangeEvent<InputAPI>): void {
    const value = event.value as unknown as string;
    const disableMedicine = (value !== '');
    this.handleUnregisterMedicine(disableMedicine);
  }

  @OnBlur(Alias.INPUT_UNREGISTRED_MEDICINE)
  handleBlurInputUnregisterdMed(): void {
    this.handleBlurUnregisterMedicine();
  }

  @OnBeforeSubmit(Alias.WIZARD)
  handleBeforeSubmit(): void {
    this.prepareSubmit();
  }
}